import {
  cloneDataOrNull, DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { LegacyReceipt } from './legacy-receipt.js';
import { Dayjs } from 'dayjs';
import { ReceiptScope } from './receipt-scope.js';

export class LegacyReceiptGroup extends DataModel {
  static readonly TYPE_ID: string = '37610a51075a48d585f7a356380b70a5';
  static readonly SCHEMA_VERSION: number = 1;

  private _currentReceipt: LegacyReceipt | null;
  private _pastReceipts: LegacyReceipt[];

  private constructor(
    public readonly uniqueId: string,
    public readonly scope: ReceiptScope,
    currentReceipt: LegacyReceipt | null,
    pastReceipts: LegacyReceipt[],
  ) {
    super();
    this._currentReceipt = currentReceipt;
    this._pastReceipts = pastReceipts;
  }

  get $typeId() {
    return LegacyReceiptGroup.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LegacyReceiptGroup.SCHEMA_VERSION);
  }

  static scopeToUniqueId(scope: ReceiptScope): string {
    return fromEnumAsString(scope).toLowerCase();
  }

  static create({ uniqueId, scope, currentReceipt, pastReceipts }: {
    uniqueId: string,
    scope: ReceiptScope,
    currentReceipt: LegacyReceipt | null,
    pastReceipts: LegacyReceipt[],
  }): LegacyReceiptGroup {
    return new LegacyReceiptGroup(
        uniqueId,
        scope,
        currentReceipt,
        [...pastReceipts],
    );
  }

  static fromLegacy(currentReceipt: LegacyReceipt | null, pastReceipts: LegacyReceipt[]): LegacyReceiptGroup {
    return this.create({
      uniqueId: this.scopeToUniqueId(ReceiptScope.Order),
      scope: ReceiptScope.Order,
      currentReceipt,
      pastReceipts,
    });
  }

  static forGenericReceipt(groupId: string): LegacyReceiptGroup {
    return new LegacyReceiptGroup(
        groupId,
        ReceiptScope.Generic,
        null,
        [],
    );
  }

  static forLegacyReceipt(scope: ReceiptScope): LegacyReceiptGroup {
    return new LegacyReceiptGroup(
        this.scopeToUniqueId(scope),
        scope,
        null,
        [],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyReceiptGroup {
    return new LegacyReceiptGroup(
        MapUtils.getString(map, 'uniqueId'),
        toEnumFromString(MapUtils.getString(map, 'scope'), ReceiptScope),
        context.fromDataOrNull<LegacyReceipt>(MapUtils.getRecordOrNull(map, 'currentReceipt')),
        MapUtils.getList<LegacyReceipt>(map, 'pastReceipts', (value) => {
          return context.fromData<LegacyReceipt>(value as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'scope': fromEnumAsString(this.scope),
      'currentReceipt': context.toDataOrNull(this._currentReceipt),
      'pastReceipts': this._pastReceipts.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new LegacyReceiptGroup(
        this.uniqueId,
        this.scope,
        cloneDataOrNull(this._currentReceipt),
        this._pastReceipts.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: LegacyReceiptGroup): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
      this.scope === other.scope &&
      isSameData(other._currentReceipt, this._currentReceipt) &&
      isSameList(other._pastReceipts, this._pastReceipts, isSameData);
  }

  getNumberOfPastReceipt(): number {
    return this._pastReceipts.length;
  }

  getPastReceipts(): LegacyReceipt[] {
    return this._pastReceipts;
  }

  hasActiveReceipt(): boolean {
    return this._currentReceipt != null;
  }

  getActiveReceipt(): LegacyReceipt | null {
    return this._currentReceipt;
  }

  setActiveReceipt(receipt: LegacyReceipt): void {
    this._currentReceipt = receipt;
  }

  getReceiptForCheckout(): LegacyReceipt | null {
    return this._currentReceipt;
  }

  getReceiptsForExport(): LegacyReceipt[] {
    const allReceipts: LegacyReceipt[] = [];

    if(this._currentReceipt != null) {
      allReceipts.push(this._currentReceipt);
    }

    allReceipts.push(...this._pastReceipts);

    return allReceipts;
  }

  markActiveReceiptAsCancelled(cancelledDownloadUrl: string, cancellationDate: Dayjs): void {
    if(this._currentReceipt != null) {
      const cancelledReceipt = this._currentReceipt.copyAsCancelledOrClone(cancelledDownloadUrl, cancellationDate);

      this._currentReceipt = null;
      this._pastReceipts.push(cancelledReceipt);
    }
  }

  toDiagnostics(): SerializedData {
    return {
      'currentReceipt': this._currentReceipt != null ? {
        'uniqueId': this._currentReceipt.uniqueId,
        'name': this._currentReceipt.name,
        'downloadUrl': this._currentReceipt.downloadUrl,
      } : null,
      'pastReceipts': this._pastReceipts.map((e) => ({
        'uniqueId': e.uniqueId,
        'name': e.name,
        'downloadUrl': e.downloadUrl,
      })),
    };
  }
}

export class LegacyReceiptGroupBuilder extends DataModelBuilder<LegacyReceiptGroup> {
  build(context: SerializationContext, map: SerializedData): LegacyReceiptGroup {
    return LegacyReceiptGroup.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyReceiptGroup.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyReceiptGroup.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
